import React from 'react';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ProductExpiryStatusDTO, ProductExpiryStatusResponseDTO } from "../../models/productbatch-model";
import styles from './styles/productbatchexpire-datatable.module.css';
import { Tooltip } from 'primereact/tooltip';

interface ProductBatchExpireDataTableProps {
  data: ProductExpiryStatusResponseDTO;
  isLoading?: boolean;
  sortField?: string | undefined;
  sortOrder?: 0 | 1 | -1 | null | undefined;
  onPageChange: (pageNumber: number, pageSize: number) => void;
  onSort: (sortColumn: string, sortDirection: string) => void;
  onRefresh?: () => void;
}

const ProductBatchExpireDataTable: React.FC<ProductBatchExpireDataTableProps> = ({
  data,
  isLoading = false,
  sortField = undefined,
  sortOrder = undefined,
  onPageChange,
  onSort,
  onRefresh,
}) => {
  const getRowClassName = (rowData: ProductExpiryStatusDTO) => {
    const expiresInDays = rowData.expiresInDays;
    
    if (expiresInDays < 0) {
      return styles['alert-row-expiring']; // Already expired
    }
    if (expiresInDays <= 30) {
      return styles['alert-row-warning']; // Warning: expiring soon
    }
    return '';
  };

  const getStatusBadgeClass = (rowData: ProductExpiryStatusDTO) => {
    const expiresInDays = rowData.expiresInDays;
    
    if (expiresInDays < 0) {
      return styles.expiryStatusExpired;
    }
    if (expiresInDays <= 30) {
      return styles.expiryStatusExpiring;
    }
    return styles.expiryStatusNormal;
  };

  const expiryStatusBodyTemplate = (rowData: ProductExpiryStatusDTO) => {
    const expiresInDays = rowData.expiresInDays;
    let label = '';
    let icon = '';

    if (expiresInDays < 0) {
      label = 'Expired';
      icon = 'pi-times-circle';
    } else if (expiresInDays === 0) {
      label = 'Today';
      icon = 'pi-exclamation-circle';
    } else if (expiresInDays <= 30) {
      label = `Expiring in ${expiresInDays} days`;
      icon = 'pi-exclamation-triangle';
    } else {
      label = 'Normal';
      icon = 'pi-check-circle';
    }

    return (
      <div className={getStatusBadgeClass(rowData)} style={{ padding: '6px 12px', borderRadius: '20px', display: 'inline-block' }}>
        <i className={`pi ${icon}`} style={{ marginRight: '6px' }}></i>
        {label}
      </div>
    );
  };

  const dateBodyTemplate = (rowData: ProductExpiryStatusDTO, field: 'manufactureDateDate' | 'expiryDate') => {
    const date = field === 'expiryDate' ? rowData.expiryDate : rowData.manufactureDate;
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const alertIconTemplate = (rowData: ProductExpiryStatusDTO) => {
    const expiresInDays = rowData.expiresInDays;
    
    if (expiresInDays < 0) {
      return (
        <i
        className={`pi pi-exclamation-circle expiry-alert-icon ${styles.expiryExpiryAlert} text-red-500`}
        data-pr-tooltip="Product has expired"
      />
      );
    }
    if (expiresInDays <= 30 && expiresInDays >= 0) {
      return (
        <i
        className={`pi pi-exclamation-triangle expiry-alert-icon ${styles.expiryExpiryAlert} text-yellow-500`}
        data-pr-tooltip={`Expires in ${expiresInDays} days`}
         />
      );
    }
    return null;
  };

  const stockBodyTemplate = (rowData: ProductExpiryStatusDTO) => {
    return (
      <div>
        <span style={{ fontWeight: 600 }}>{rowData.currentStock}</span>
        {/* <span style={{ color: '#6b7280', marginLeft: '4px' }}>units</span> */}
      </div>
    );
  };

  const handlePageChange = (event: DataTablePageEvent) => {
    const pageNumber = (event.page ?? 0) + 1;
    const pageSize = event.rows ?? 10;
    onPageChange(pageNumber, pageSize);
  };

  const handleSort = (event: any) => {
    // sortOrder: 1 = ASC, -1 = DESC, 0 = no sort (cleared)
    if (event.sortField && event.sortOrder !== 0 && event.sortOrder !== undefined) {
      const sortDirection = event.sortOrder === 1 ? 'ASC' : 'DESC';
      onSort(event.sortField, sortDirection);
    }
  };

  return (
    <div className={`${styles.expiryDatatableContainer} productbatchexpire-datatable`}>
      <div className={styles.expiryTableCardWrapper}>

          {/* ✅ PrimeReact Tooltip */}
      <Tooltip target=".expiry-alert-icon" position="top" />
        {/* Table Header */}
        {/* <div className={styles.expiryTableHeader}>
          <div className={styles.expiryTableHeaderLeft}>
            <div className={styles.expiryTableIcon}>
              <i className="pi pi-box"></i>
            </div>
            <div>
              <h3 className={styles.expiryTableTitle}>Product Batch Expiry Status</h3>
            </div>
          </div>
          <div className={styles.expiryTableHeaderRight}>
             <span className={styles.expiryTableHeaderStatBadge}>
                <i className="pi pi-list"></i>
                {data.totalRecords} Records
              </span>
            <Button
              icon="pi pi-refresh"
              rounded
              text
              severity="secondary"
              onClick={onRefresh}
              className={styles.expiryRefreshBtn}
              loading={isLoading}
              title="Refresh data"
            />
          </div>
        </div> */}

        {/* Data Table */}
        <DataTable
          value={data.data}
          loading={isLoading}
          paginator
          rows={data.pageSize}
          first={(data.pageNumber - 1) * data.pageSize}
          totalRecords={data.totalRecords}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          onPage={handlePageChange}
          onSort={handleSort}
          sortMode="single"
          sortField={sortField}
          sortOrder={sortOrder}
          rowClassName={(rowData) => getRowClassName(rowData)}
          emptyMessage={
            <div className={styles.expiryNoDataContainer}>
              <div className={styles.expiryNoDataIcon}>
                <i className="pi pi-inbox"></i>
              </div>
              <p>No product batches found matching your criteria</p>
            </div>
          }
          responsiveLayout="scroll"
        >
          {/* Alert Icon Column */}
          <Column
            header=""
            body={(rowData) => alertIconTemplate(rowData)}
            style={{ width: '40px', textAlign: 'center' }}
          />

          {/* POS Code */}
          <Column
            field="posCode"
            header="POS"
            style={{ minWidth: '100px' }}
            sortable
            sortField="PosCode"
          />

          {/* Product Name */}
          <Column
            field="productName"
            header="Product"
            style={{ minWidth: '100px' }}
            sortable
            sortField="ProductName"
          />

          {/* Category */}
          <Column
            field="category"
            header="Category"
            style={{ minWidth: '100px' }}
            sortable
            sortField="Category"
          />

          {/* Sub Category */}
          {/* <Column
            field="subCategory"
            header="Sub Category"
            style={{ minWidth: '100px' }}
            sortable
          /> */}

          {/* Batch No */}
          <Column
            field="batchNo"
            header="Batch No"
            style={{ minWidth: '100px' }}
            sortable
            sortField="BatchNo"
          />

          {/* Manufacture Date */}
          <Column
            field="manufactureDate"
            header="Manufacture Date"
            body={(rowData) => dateBodyTemplate(rowData, 'manufactureDateDate')}
            style={{ minWidth: '120px' }}
            sortable
            sortField="ManufactureDate"
          />

          {/* Expiry Date */}
          <Column
            field="expiryDate"
            header="Expiry Date"
            body={(rowData) => dateBodyTemplate(rowData, 'expiryDate')}
            style={{ minWidth: '120px' }}
            sortable
            sortField="ExpiryDate"
          />

          {/* Current Stock */}
          <Column
            field="currentStock"
            header="Current Stock"
            body={stockBodyTemplate}
            style={{ minWidth: '100px', textAlign: 'center' }}
            sortable
            sortField="CurrentStock"
          />

          {/* Expires In Days */}
          <Column
            field="expiresInDays"
            header="Expires In (Days)"
            body={(rowData) => {
              const days = rowData.expiresInDays;
              const color = days < 0 ? '#DC2626' : days <= 30 ? '#EA580C' : '#10492a';
              return (
                <span style={{ color, fontWeight: 600 }}>
                  {days < 0 ? `${Math.abs(days)} days ago` : `${days} days`}
                </span>
              );
            }}
            style={{ minWidth: '120px', textAlign: 'right' }}
            sortable
            sortField="ExpiresInDays"
          />

          {/* Status */}
          <Column
            header="Status"
            body={expiryStatusBodyTemplate}
            style={{ minWidth: '150px', textAlign: 'center' }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default ProductBatchExpireDataTable;
